import React, { useState } from 'react';

interface BulkUploadModalProps {
  title: string;
  onClose: () => void;
  onUpload: (file: File) => Promise<any>;
  onSuccess: () => void;
}

const FIELD_DOCS: Record<string, { required: string[]; optional: string[]; csvExample: string; jsonExample: string }> = {
  Programs: {
    required: ['name_ar', 'name_en'],
    optional: ['faculty', 'aliases (comma-separated)'],
    csvExample: `name_ar,name_en,faculty,aliases
علوم الحاسوب,Computer Science,كلية العلوم,"CS,حاسوب"
هندسة البرمجيات,Software Engineering,كلية الهندسة,`,
    jsonExample: `[
  {
    "name_ar": "علوم الحاسوب",
    "name_en": "Computer Science",
    "faculty": "كلية العلوم",
    "aliases": "CS,حاسوب"
  }
]`,
  },
  Contacts: {
    required: ['unit'],
    optional: ['name_ar', 'name_en', 'phone', 'email', 'hours', 'location'],
    csvExample: `unit,name_ar,name_en,phone,email,hours,location
عمادة القبول والتسجيل,أحمد محمد,Ahmad Mohammad,+970-9-234-5678,ahmad@najah.edu,Sun-Thu 8-4,New Campus Bldg A`,
    jsonExample: `[
  {
    "unit": "عمادة القبول والتسجيل",
    "name_ar": "أحمد محمد",
    "phone": "+970-9-234-5678",
    "email": "ahmad@najah.edu"
  }
]`,
  },
  Departments: {
    required: ['name_ar', 'name_en'],
    optional: ['head_name', 'contact_email', 'contact_phone'],
    csvExample: `name_ar,name_en,head_name,contact_email,contact_phone
قسم علوم الحاسوب,Computer Science Dept,د. سامي,cs@najah.edu,+970-9-234-5000`,
    jsonExample: `[
  {
    "name_ar": "قسم علوم الحاسوب",
    "name_en": "Computer Science Dept",
    "head_name": "د. سامي",
    "contact_email": "cs@najah.edu"
  }
]`,
  },
  'Admission Grades': {
    required: ['program_id', 'year', 'grade'],
    optional: ['admission_type (general | parallel | international)', 'track (scientific | literary | commercial | industrial)'],
    csvExample: `program_id,year,grade,admission_type,track
3,2025,85.5,general,scientific
3,2025,78.2,parallel,scientific`,
    jsonExample: `[
  {
    "program_id": 3,
    "year": 2025,
    "grade": 85.5,
    "admission_type": "general",
    "track": "scientific"
  }
]`,
  },
};

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  title,
  onClose,
  onUpload,
  onSuccess
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showFormat, setShowFormat] = useState<'csv' | 'json'>('csv');

  // Extract entity type from title like "Bulk Upload Programs"
  const entityType = title.replace('Bulk Upload ', '');
  const docs = FIELD_DOCS[entityType];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.json')) {
        setFile(droppedFile);
        setResult(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const uploadResult = await onUpload(file);
      setResult(uploadResult);
      
      if (uploadResult.success) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (error: any) {
      alert('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-modal max-w-2xl w-full mx-4 overflow-hidden animate-slide-up max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
              <svg className="w-4 h-4 text-primary-700" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-primary-800">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-primary-100 flex items-center justify-center transition-colors"
            disabled={uploading}
          >
            <svg className="w-4 h-4 text-primary-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto">
          {!result ? (
            <>
              {/* Drop Zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                  dragOver 
                    ? 'border-primary bg-primary-50/50' 
                    : file 
                      ? 'border-emerald-300 bg-emerald-50/50' 
                      : 'border-primary-200 hover:border-primary-300 bg-primary-50/50'
                }`}
              >
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                {file ? (
                  <div>
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-primary-800">{file.name}</p>
                    <p className="text-xs text-primary-500 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div>
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mx-auto mb-2">
                      <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-primary-700">Drop your file here or click to browse</p>
                    <p className="text-xs text-primary-400 mt-1">Supports CSV and JSON files</p>
                  </div>
                )}
              </div>

              {/* Field Documentation */}
              {docs && (
                <div className="mt-4 rounded-lg border border-primary-200 overflow-hidden">
                  {/* Required / Optional fields */}
                  <div className="px-4 py-3 bg-primary-50 border-b border-primary-100">
                    <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-xs">
                      <div>
                        <span className="font-semibold text-primary-600">Required: </span>
                        {docs.required.map((f, i) => (
                          <span key={f}>
                            <code className="px-1 py-0.5 bg-red-50 text-red-700 rounded font-mono text-xs">{f}</code>
                            {i < docs.required.length - 1 && <span className="text-primary-300 mx-1">,</span>}
                          </span>
                        ))}
                      </div>
                      <div>
                        <span className="font-semibold text-primary-600">Optional: </span>
                        {docs.optional.map((f, i) => (
                          <span key={f}>
                            <code className="px-1 py-0.5 bg-primary-100 text-primary-600 rounded font-mono text-xs">{f}</code>
                            {i < docs.optional.length - 1 && <span className="text-primary-300 mx-1">,</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Format toggle + example */}
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-1 mb-2">
                      <button
                        onClick={() => setShowFormat('csv')}
                        className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                          showFormat === 'csv'
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-primary-400 hover:text-primary-600'
                        }`}
                      >
                        CSV
                      </button>
                      <button
                        onClick={() => setShowFormat('json')}
                        className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                          showFormat === 'json'
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-primary-400 hover:text-primary-600'
                        }`}
                      >
                        JSON
                      </button>
                      <span className="text-xs text-primary-400 ml-2">Example format</span>
                    </div>
                    <pre className="text-xs font-mono text-primary-600 bg-primary-50 rounded-lg p-3 overflow-x-auto leading-relaxed whitespace-pre">
                      {showFormat === 'csv' ? docs.csvExample : docs.jsonExample}
                    </pre>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className={`rounded-xl p-5 ${result.success ? 'bg-emerald-50 border border-emerald-100' : 'bg-amber-50 border border-amber-100'}`}>
              <div className="flex items-center gap-2 mb-3">
                {result.success ? (
                  <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                )}
                <h4 className={`text-sm font-semibold ${result.success ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {result.success ? 'Upload Successful' : 'Completed with Errors'}
                </h4>
              </div>
              <div className={`text-xs space-y-1 ${result.success ? 'text-emerald-700' : 'text-amber-700'}`}>
                {result.created > 0 && <p>Created: {result.created} items</p>}
                {result.updated > 0 && <p>Updated: {result.updated} items</p>}
                {result.deleted > 0 && <p>Deleted: {result.deleted} items</p>}
                
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <p className="font-medium mb-1">Errors ({result.errors.length}):</p>
                    <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                      {result.errors.map((error: string, idx: number) => (
                        <li key={idx} className="text-xs flex items-start gap-1">
                          <span className="text-amber-400 mt-px">-</span>
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-primary-100 flex justify-end gap-2 flex-shrink-0">
          {!result ? (
            <>
              <button onClick={onClose} className="btn-secondary text-xs" disabled={uploading}>
                Cancel
              </button>
              <button
                onClick={handleUpload}
                className="btn-primary text-xs gap-1.5"
                disabled={!file || uploading}
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                    Upload
                  </>
                )}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="btn-primary text-xs">Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
